import { useMemo, useState } from 'react';
import { Search, X, ArrowLeft } from 'lucide-react';

import { useThemes } from '@/api/usePresentationWizard';
import { Button } from '@/components/ui';
import type { EventTheme } from '@/types/wizard';
import { ThemeCard } from './ThemeCard';
import styles from './SelectThemeStep.module.css';

const CATEGORY_FILTERS: { label: string; match: string[] }[] = [
  { label: 'Corporate', match: ['corporate', 'executive', 'professional'] },
  { label: 'Holiday', match: ['seasonal', 'christmas', 'new-year', 'halloween', 'easter'] },
  { label: 'Party', match: ['party', 'nightlife', 'disco'] },
  { label: 'Celebration', match: ['celebration', 'gala', 'anniversary', 'birthday'] },
  { label: 'Sports', match: ['sports', 'competition', 'fitness', 'active'] },
  { label: 'Cultural', match: ['cultural', 'tradition', 'heritage', 'folklore'] },
  { label: 'Family', match: ['family', 'kids', 'baby'] },
  { label: 'Tech', match: ['tech', 'innovation', 'hackathon', 'futuristic'] },
  { label: 'Elegant', match: ['elegant', 'luxury', 'premium'] },
  { label: 'Aesthetic', match: ['aesthetic', 'minimalist', 'bohemian', 'rustic', 'vintage'] },
];

interface Props {
  selected: string[];
  onConfirm: (slugs: string[]) => void;
  onBack?: () => void;
  title?: string;
  subtitle?: string;
  templateSlug?: string;
}

export const SelectThemeStep = ({
  selected,
  onConfirm,
  onBack,
  title = 'Select Event Theme',
  subtitle = 'Choose one or more themes for your event',
  templateSlug,
}: Props) => {
  const { data: themes, isLoading } = useThemes();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>(selected);

  const togglePick = (slug: string) => {
    setPicked((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const removePick = (slug: string) => {
    setPicked((prev) => prev.filter((s) => s !== slug));
  };

  const toggleCategory = (label: string) => {
    setActiveCategory((prev) => (prev === label ? null : label));
  };

  const clearFilters = () => {
    setSearch('');
    setActiveCategory(null);
  };

  // Map slugs to names for the selection bar
  const pickedNames = useMemo(() => {
    if (!themes) return new Map<string, string>();
    const map = new Map<string, string>();
    themes.forEach((t) => {
      if (picked.includes(t.slug)) map.set(t.slug, t.name);
    });
    return map;
  }, [themes, picked]);

  const filtered = useMemo(() => {
    if (!themes) return [];

    let result = themes;

    const q = search.toLowerCase().trim();
    if (q) {
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.tags.some((tag) => tag.toLowerCase().includes(q)),
      );
    }

    if (activeCategory) {
      const cat = CATEGORY_FILTERS.find((c) => c.label === activeCategory);
      if (cat) {
        result = result.filter((t) => t.tags.some((tag) => cat.match.includes(tag)));
      }
    }

    if (templateSlug) {
      const related: EventTheme[] = [];
      const rest: EventTheme[] = [];
      result.forEach((t) => {
        if (t.related_templates?.includes(templateSlug)) {
          related.push(t);
        } else {
          rest.push(t);
        }
      });
      return [...related, ...rest];
    }

    return result;
  }, [themes, search, activeCategory, templateSlug]);

  const hasRelated =
    templateSlug && filtered.some((t) => t.related_templates?.includes(templateSlug));
  const hasActiveFilters = search.length > 0 || activeCategory !== null;

  if (isLoading) return <div className={styles.loading}>Loading themes...</div>;

  return (
    <div className={styles.container}>
      {onBack && (
        <button className={styles.backButton} onClick={onBack}>
          <ArrowLeft size={18} /> Back
        </button>
      )}
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.searchWrapper}>
          <Search size={15} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search themes..."
          />
          {hasActiveFilters && (
            <button className={styles.clearBtn} onClick={clearFilters} aria-label="Clear filters">
              <X size={14} />
            </button>
          )}
        </div>

        <div className={styles.categories}>
          {CATEGORY_FILTERS.map((cat) => (
            <button
              key={cat.label}
              className={`${styles.catChip} ${activeCategory === cat.label ? styles.catChipActive : ''}`}
              onClick={() => toggleCategory(cat.label)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Selected themes bar */}
      {picked.length > 0 && (
        <div className={styles.selectionBar}>
          <span className={styles.selectionLabel}>{picked.length} selected:</span>
          {picked.map((slug) => (
            <span key={slug} className={styles.selectionChip}>
              {pickedNames.get(slug) ?? slug}
              <button
                className={styles.selectionRemove}
                onClick={() => removePick(slug)}
                aria-label={`Remove ${pickedNames.get(slug) ?? slug}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.scrollContainer}>
        {filtered.length === 0 ? (
          <p className={styles.noResults}>No themes match your filters.</p>
        ) : (
          <>
            {hasRelated && (
              <p className={styles.relatedHint}>Recommended for this template shown first</p>
            )}
            <div className={styles.grid}>
              {filtered.map((t) => (
                <ThemeCard
                  key={t.slug}
                  theme={t}
                  isSelected={picked.includes(t.slug)}
                  onClick={() => togglePick(t.slug)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className={styles.actions}>
        {picked.length > 0 ? (
          <Button variant="primary" onClick={() => onConfirm(picked)}>
            Continue with {picked.length} theme{picked.length > 1 ? 's' : ''} &rarr;
          </Button>
        ) : (
          <p className={styles.hint}>Select at least one theme to continue</p>
        )}
      </div>
    </div>
  );
};
